import * as React from 'react';
import { toast } from 'sonner';
import { Plus, Tags } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { DataTable } from '../../../design-system/components/DataTable';
import { EmptyState } from '../../../design-system/components/EmptyState';
import { useApi } from '../../../design-system/useApi';
import { VISUAL, statusPill, statusDot } from '../../../design-system/visual';
import { hrApi } from '../api';
import type { LeaveTypeRow } from '../types';

const EMPTY_DRAFT = { name: '', code: '', is_paid: true, allowance: '0' };

/** HR management of configurable leave types — paid/unpaid + annual allowance. */
export function LeaveTypesPanel() {
  const confirm = useConfirm();
  const { data, loading, reload } = useApi<{ types: LeaveTypeRow[] }>('/api/hr/leave-types');
  const [draft, setDraft] = React.useState({ ...EMPTY_DRAFT });
  // The single form does double duty: create when null, edit when set.
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  function startEdit(t: LeaveTypeRow) {
    setEditingId(t.id);
    setDraft({
      name: t.name,
      code: t.code,
      is_paid: t.is_paid,
      allowance: String(Number(t.default_annual_allowance) || 0),
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setDraft({ ...EMPTY_DRAFT });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.name.trim() || !draft.code.trim()) { toast.error('Name and code are required'); return; }
    const payload = {
      name: draft.name.trim(),
      code: draft.code.trim().toLowerCase(),
      is_paid: draft.is_paid,
      default_annual_allowance: Number(draft.allowance) || 0,
    };
    setSaving(true);
    try {
      if (editingId) {
        await hrApi.updateLeaveType(editingId, payload);
        toast.success('Leave type updated');
      } else {
        await hrApi.createLeaveType(payload);
        toast.success('Leave type added');
      }
      cancelEdit();
      reload();
    } catch (err: any) { toast.error(editingId ? 'Could not update' : 'Could not add', { description: err.message }); }
    finally { setSaving(false); }
  }

  async function toggle(t: LeaveTypeRow) {
    try {
      await hrApi.updateLeaveType(t.id, { is_active: !t.is_active });
      reload();
    } catch (err: any) { toast.error('Could not update', { description: err.message }); }
  }

  async function del(id: string) {
    if (!(await confirm({ title: 'Delete this leave type?', description: 'Existing balances for it are removed.', destructive: true, confirmLabel: 'Delete' }))) return;
    try {
      await hrApi.deleteLeaveType(id);
      toast.success('Deleted');
      if (id === editingId) cancelEdit();
      reload();
    } catch (err: any) { toast.error('Delete failed', { description: err.message }); }
  }

  return (
    <div className="space-y-6">
      <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
        <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>
          {editingId ? 'Edit leave type' : 'New leave type'}
        </h3>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="grid gap-1.5 sm:col-span-2">
            <Label>Name</Label>
            <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Annual leave" />
          </div>
          <div className="grid gap-1.5">
            <Label>Code</Label>
            <Input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} placeholder="annual" />
          </div>
          <div className="grid gap-1.5">
            <Label>Allowance (days/yr)</Label>
            <Input type="number" min={0} step="0.5" value={draft.allowance} onChange={(e) => setDraft({ ...draft, allowance: e.target.value })} />
          </div>
          <label className="flex items-center gap-2.5 cursor-pointer text-[13px] text-muted-foreground sm:col-span-3">
            <input
              type="checkbox"
              checked={draft.is_paid}
              onChange={(e) => setDraft({ ...draft, is_paid: e.target.checked })}
              className="h-4 w-4 rounded border-border accent-saffron"
            />
            Paid leave
          </label>
          <div className="sm:col-span-1 flex items-end justify-end gap-2">
            {editingId && (
              <Button type="button" variant="ghost" disabled={saving} onClick={cancelEdit} className="rounded-full h-10 px-4 text-[13px] font-semibold text-muted-foreground">
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={saving} className="rounded-full h-10 px-5 text-[13px] font-semibold gap-1.5">
              {!editingId && <Plus className="h-3.5 w-3.5" />}
              {saving ? 'Saving…' : editingId ? 'Save changes' : 'Add type'}
            </Button>
          </div>
        </form>
        <p className="text-[12px] text-muted-foreground mt-3">
          A 0-day allowance means the type is untracked (no balance is deducted on approval) — useful for unpaid leave.
        </p>
      </section>

      <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
        <DataTable<LeaveTypeRow>
          loading={loading}
          rows={data?.types ?? []}
          rowKey={(r) => r.id}
          columns={[
            { key: 'name', header: 'Name', cell: (r) => <span className="font-medium text-foreground">{r.name}</span> },
            { key: 'code', header: 'Code', cell: (r) => <span className="font-mono text-[12px] text-muted-foreground">{r.code}</span> },
            { key: 'paid', header: 'Paid', cell: (r) => r.is_paid ? 'Yes' : 'No' },
            { key: 'allowance', header: 'Allowance', cell: (r) => Number(r.default_annual_allowance) > 0 ? `${Number(r.default_annual_allowance)} days` : 'Untracked' },
            { key: 'active', header: 'Active', cell: (r) => r.is_active
              ? <span className={statusPill('success')}><span className={statusDot('success')} />Active</span>
              : <span className={statusPill('muted')}><span className={statusDot('muted')} />Inactive</span> },
            { key: 'actions', header: '', cell: (r) => (
              <div className="flex gap-1 justify-end">
                <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={() => startEdit(r)}>Edit</Button>
                <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={() => toggle(r)}>{r.is_active ? 'Disable' : 'Enable'}</Button>
                <Button size="sm" variant="ghost" className="rounded-full h-8 text-muted-foreground hover:text-destructive" onClick={() => del(r.id)}>Delete</Button>
              </div>
            ) },
          ]}
          emptyState={<EmptyState icon={<Tags />} title="No leave types" description="Add a type above. Tracked types deduct from employee balances on approval." />}
        />
      </section>
    </div>
  );
}
